import { Alert, Badge, Card, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "./DataTable";
import { KeyInventoryErrorState } from "./GenerationBadge";
import { EmptyState } from "./states";
import { sessionKeyPermissionColumns } from "@/entities/configs/sessionKeyPermissions";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreQuery } from "@/firestore/hooks";
import type { ReadState } from "@/firestore/hooks";
import type { SessionKeyPermissionDoc, WalletKeyInventoryDoc, WithDocId } from "@/firestore/types";
import { entityPath } from "@/routes/paths";
import { legacyKeyCount, summarizeKeys } from "@/wallet/generations";

const COLUMNS = sessionKeyPermissionColumns({ withWallet: false });

/**
 * Wallet 360's Keys panel: every session key of the wallet with its on-chain permission (flag,
 * signer module) and whether it is an ERC-1271 exposure, read from the admin-only
 * `sessionKeyPermissions` projection — the only source of `kmsKeyDestroyedAt` (the wallet summary's
 * entries don't carry it). A key is exposed while its permission answers ERC-1271 and its KMS
 * material is not scheduled for destruction, whatever its state: a revoked key can be exposed.
 * Operator visibility only: these internals never reach the owner-readable `sessionKeys` document
 * or the apps.
 */
export function SessionKeyPermissionsCard({
  walletId,
  inventory,
}: {
  walletId: string;
  inventory: ReadState<WithDocId<WalletKeyInventoryDoc> | null>;
}) {
  const navigate = useNavigate();
  const permissions = useFirestoreQuery<SessionKeyPermissionDoc>(
    COLLECTIONS.sessionKeyPermissions,
    {
      filters: [["account", "==", walletId]],
      orderBy: [["createdAt", "desc"]],
      limit: 200,
    },
  );
  const summary = summarizeKeys(permissions.data);
  const inventoryCount = inventory.data ? legacyKeyCount(inventory.data) : null;
  const countMismatch =
    !permissions.loading &&
    !permissions.error &&
    inventoryCount !== null &&
    inventoryCount !== summary.exposed;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text fw={600}>Session keys and permissions</Text>
        {!permissions.loading && !permissions.error ? (
          <Group gap={6}>
            <Badge variant="light" color="gray" tt="none">
              {summary.total} keys · {summary.enabled} enabled
            </Badge>
            <Badge
              variant={summary.exposed > 0 ? "filled" : "light"}
              color={summary.exposed > 0 ? "red" : "green"}
              tt="none"
            >
              {summary.exposed} 1271-capable (exposed)
            </Badge>
            {summary.exposedRevoked > 0 ? (
              <Badge variant="light" color="red" tt="none">
                {summary.exposedRevoked} revoked, still signature-capable
              </Badge>
            ) : null}
            {summary.destroyScheduled > 0 ? (
              <Badge variant="light" color="yellow" tt="none">
                {summary.destroyScheduled} legacy, KMS destruction scheduled
              </Badge>
            ) : null}
          </Group>
        ) : null}
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Each key&apos;s on-chain permission. A legacy key (enabled before the permission pin)
        answers ERC-1271 for its account, so whoever can sign with it, the backend included, could
        sign the account&apos;s USDC away. Revoking is off-chain only: the key stays an exposure
        until its KMS destruction is scheduled, and the permission stays installed until the wallet
        migrates to v3.8.
      </Text>

      {permissions.error ? (
        <KeyInventoryErrorState error={permissions.error} />
      ) : (
        <Stack gap="sm">
          {summary.exposed > 0 ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
              {plural(summary.exposed, "key")} on this wallet can still sign an ERC-1271
              transferWithAuthorization for the account&apos;s USDC ({summary.exposedEnabled}{" "}
              enabled, {summary.exposedRevoked} revoked with KMS material not yet scheduled for
              destruction). This wallet is a v3.8 migration candidate.
            </Alert>
          ) : null}
          {countMismatch ? (
            <Text size="xs" c="orange">
              The wallet&apos;s key summary counts {plural(inventoryCount ?? 0, "exposed key")}, the
              keys below {summary.exposed}: a projection is lagging or the backfill has not run.
            </Text>
          ) : null}
          <DataTable
            data={permissions.data}
            columns={COLUMNS}
            loading={permissions.loading}
            searchable={false}
            onRowClick={(row) => navigate(entityPath("sessionKey", row.id ?? row._docId))}
            emptyState={
              <EmptyState
                title="No session keys"
                description="No session-key permission is projected for this wallet."
              />
            }
          />
        </Stack>
      )}
    </Card>
  );
}
